import { inject, Injectable } from '@angular/core';
import { OverlayHandler } from '@core/handler/overlay.handler';
import { ChargeRepayErrorCodes, PCIAMErrorCodes } from '@shared/errors/error.codes';
import { CRError } from '@shared/models/error.models';
import {
  addEdiKeyError,
  alreadyExportedError,
  assignSeatError,
  automaticExportActiveError,
  automaticExportMissmatchError,
  carNotFoundError,
  changePasswordError,
  createPricePolicyError,
  deleteDriverInvitationError,
  deleteMemberError,
  deleteReceiverError,
  downloadChargingEventsCsvError,
  downloadExportError,
  ediKeyExistsError,
  ediKeyStillInUseError,
  exportError,
  getBackendProviderError,
  getChargePointDetailError,
  getChargePointsError,
  getChargingEventsError,
  getChargingStationBackendDataError,
  getConfigCodeError,
  getDriverKeysError,
  getDriverOrganizationError,
  getEnergyContractError,
  getFlatratePricesError,
  getLatestPriceError,
  getManagersError,
  getMileagesError,
  getOrganizationsError,
  getReimbursementsError,
  getSubscriptionsError,
  acceptInvitationError,
  invalidInvitationLinkError,
  invalidInputError,
  invitationExistsError,
  inviteDriverError,
  inviteMemberError,
  OverlayConfig,
  passwordPolicyError,
  refreshTokenError,
  registerUserError,
  removeEdiKeyError,
  resetPasswordRequestError,
  saveChargingStationBackendDataError,
  saveDriverKeyError,
  securityTokenMissingError,
  testBackendProviderConnectionError,
  unassignSeatError,
  unknownError,
  userNotFoundError,
  updateAutomaticCompanyCarError,
  updateBackendProviderError,
  updateLanguageError,
  updateProfileError,
  updatingSettingsError,
  upgradeSubscriptionError,
} from '@shared/models/overlay.models';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandler {
  private readonly auth = inject(AuthService);
  private readonly overlayService = inject(OverlayHandler);

  public handleError(error: CRError): void {
    const overlay = this.mapErrorToOverlay(error.errorCode);

    this.overlayService.showOverlay(overlay);

    if (error.severity === 'panic') {
      // Give the user time to read the error before logging out
      setTimeout(() => void this.auth.logout(), 5000);
    }
  }

  private mapErrorToOverlay(errorCode: ChargeRepayErrorCodes | PCIAMErrorCodes): OverlayConfig {
    let dialogConfig;
    switch (errorCode) {
      case PCIAMErrorCodes.GET_ORGANIZATIONS_ERROR:
        dialogConfig = getOrganizationsError;
        break;
      case ChargeRepayErrorCodes.GET_CHARGEPOINTS_ERROR:
        dialogConfig = getChargePointsError;
        break;
      case ChargeRepayErrorCodes.GET_SUBSCRIPTIONS_ERROR:
        dialogConfig = getSubscriptionsError;
        break;
      case ChargeRepayErrorCodes.GET_DRIVER_ORGANIZATION_ERROR:
        dialogConfig = getDriverOrganizationError;
        break;
      case ChargeRepayErrorCodes.INVITE_DRIVER_ERROR:
        dialogConfig = inviteDriverError;
        break;
      case ChargeRepayErrorCodes.INVITATION_USER_ALREADY_INVITED:
        dialogConfig = invitationExistsError;
        break;
      case ChargeRepayErrorCodes.REMOVE_EDI_KEY_ERROR:
        dialogConfig = removeEdiKeyError;
        break;
      case ChargeRepayErrorCodes.EDI_KEY_STILL_IN_USE:
        dialogConfig = ediKeyStillInUseError;
        break;
      case ChargeRepayErrorCodes.ADD_EDI_KEY_ERROR:
        dialogConfig = addEdiKeyError;
        break;
      case ChargeRepayErrorCodes.EDI_KEY_EXISTS_ERROR:
        dialogConfig = ediKeyExistsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_PAYMENT_PROVIDER_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_MILEAGE_REQUEST_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_MILEAGE_SETTINGS_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_CALIBRATION_LAW_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_EXTERNAL_BACKEND_PROVIDER_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.GET_BACKEND_PROVIDER_ERROR:
        dialogConfig = getBackendProviderError;
        break;
      case ChargeRepayErrorCodes.UPDATE_BACKEND_PROVIDER_ERROR:
        dialogConfig = updateBackendProviderError;
        break;
      case PCIAMErrorCodes.GET_MANAGERS_ERROR:
        dialogConfig = getManagersError;
        break;
      case PCIAMErrorCodes.DELETE_MEMBER_ERROR:
        dialogConfig = deleteMemberError;
        break;
      case PCIAMErrorCodes.INVITE_MANAGER_ERROR:
        dialogConfig = inviteMemberError;
        break;
      case ChargeRepayErrorCodes.INVALID_INPUT_ERROR:
        dialogConfig = invalidInputError;
        break;
      case ChargeRepayErrorCodes.UPGRADE_SUBSCRIPTION_ERROR:
        dialogConfig = upgradeSubscriptionError;
        break;
      case ChargeRepayErrorCodes.UPDATE_RFID_CHARGING_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.UPDATE_AUTOMATIC_COMPANY_CAR_ERROR:
        dialogConfig = updateAutomaticCompanyCarError;
        break;
      case ChargeRepayErrorCodes.DELETE_RECEIVER_ERROR:
        dialogConfig = deleteReceiverError;
        break;
      case ChargeRepayErrorCodes.UPDATE_CHARGE_ASSIGN_SETTINGS_ERROR:
        dialogConfig = updatingSettingsError;
        break;
      case ChargeRepayErrorCodes.CHARGE_ASSIGN_TIME_AUTOMATIC_EXPORT_MISMATCH:
        dialogConfig = automaticExportMissmatchError;
        break;
      case ChargeRepayErrorCodes.GET_CHARGING_EVENTS_ERROR:
        dialogConfig = getChargingEventsError;
        break;
      case ChargeRepayErrorCodes.DOWNLOAD_CHARGING_EVENTS_CSV_ERROR:
        dialogConfig = downloadChargingEventsCsvError;
        break;
      case ChargeRepayErrorCodes.DOWNLOAD_EXPORT_ERROR:
        dialogConfig = downloadExportError;
        break;
      case ChargeRepayErrorCodes.ALREADY_EXPORTED_ERROR:
        dialogConfig = alreadyExportedError;
        break;
      case ChargeRepayErrorCodes.AUTOMATIC_EXPORT_ACTIVE:
        dialogConfig = automaticExportActiveError;
        break;
      case ChargeRepayErrorCodes.EXPORT_ERROR:
        dialogConfig = exportError;
        break;
      case PCIAMErrorCodes.UPDATE_ATTRIBUTES_ERROR:
        dialogConfig = updateProfileError;
        break;
      case PCIAMErrorCodes.UPDATE_LANGUAGE_ERROR:
        dialogConfig = updateLanguageError;
        break;
      case ChargeRepayErrorCodes.REFRESH_TOKEN_ERROR:
        dialogConfig = refreshTokenError;
        break;
      case ChargeRepayErrorCodes.GET_MILEAGES_ERROR:
        dialogConfig = getMileagesError;
        break;
      case ChargeRepayErrorCodes.CAR_NOT_FOUND_ERROR:
        dialogConfig = carNotFoundError;
        break;
      case ChargeRepayErrorCodes.GET_DRIVER_KEYS_ERROR:
        dialogConfig = getDriverKeysError;
        break;
      case ChargeRepayErrorCodes.SAVE_DRIVER_KEY_ERROR:
        dialogConfig = saveDriverKeyError;
        break;
      case ChargeRepayErrorCodes.GET_ENERGY_CONTRACT_ERROR:
        dialogConfig = getEnergyContractError;
        break;
      case ChargeRepayErrorCodes.GET_LATEST_PRICE_ERROR:
        dialogConfig = getLatestPriceError;
        break;
      case ChargeRepayErrorCodes.GET_FLATRATE_PRICES_ERROR:
        dialogConfig = getFlatratePricesError;
        break;
      case ChargeRepayErrorCodes.CREATE_PRICE_POLICY_ERROR:
        dialogConfig = createPricePolicyError;
        break;
      case ChargeRepayErrorCodes.ASSIGN_SEAT_ERROR:
        dialogConfig = assignSeatError;
        break;
      case ChargeRepayErrorCodes.UNASSIGN_SEAT_ERROR:
        dialogConfig = unassignSeatError;
        break;
      case ChargeRepayErrorCodes.DELETE_DRIVER_INVITATION_ERROR:
        dialogConfig = deleteDriverInvitationError;
        break;
      case ChargeRepayErrorCodes.USER_NOT_FOUND_ERROR:
        dialogConfig = userNotFoundError;
        break;
      case ChargeRepayErrorCodes.GET_CHARGING_STATION_BACKEND_DATA_ERROR:
        dialogConfig = getChargingStationBackendDataError;
        break;
      case ChargeRepayErrorCodes.SAVE_CHARGING_STATION_BACKEND_DATA_ERROR:
        dialogConfig = saveChargingStationBackendDataError;
        break;
      case ChargeRepayErrorCodes.GET_REIMBURSEMENTS_ERROR:
        dialogConfig = getReimbursementsError;
        break;
      case ChargeRepayErrorCodes.GET_CONFIG_CODE_ERROR:
        dialogConfig = getConfigCodeError;
        break;
      case ChargeRepayErrorCodes.GET_CHARGE_POINT_DETAIL_ERROR:
        dialogConfig = getChargePointDetailError;
        break;
      case ChargeRepayErrorCodes.TEST_BACKEND_PROVIDER_CONNECTION_ERROR:
        dialogConfig = testBackendProviderConnectionError;
        break;
      case PCIAMErrorCodes.REGISTER_USER_ERROR:
        dialogConfig = registerUserError;
        break;
      case PCIAMErrorCodes.PASSWORD_POLICY_ERROR:
        dialogConfig = passwordPolicyError;
        break;
      case PCIAMErrorCodes.RESET_PASSWORD_ERROR:
        dialogConfig = resetPasswordRequestError;
        break;
      case PCIAMErrorCodes.CHANGE_PASSWORD_ERROR:
        dialogConfig = changePasswordError;
        break;
      case PCIAMErrorCodes.SECURITY_TOKEN_MISSING:
        dialogConfig = securityTokenMissingError;
        break;
      case PCIAMErrorCodes.INVALID_INVITATION_LINK:
        dialogConfig = invalidInvitationLinkError;
        break;
      case PCIAMErrorCodes.ACCEPT_INVITATION_ERROR:
        dialogConfig = acceptInvitationError;
        break;
      default:
        dialogConfig = unknownError;
        break;
    }

    return dialogConfig;
  }
}
